import { moduleState, state } from 'cerebral'
import { difference, flatten, fromPairs, groupBy, pluck, prop, splitEvery, uniq, uniqBy } from 'ramda'
import schemaSettings from '../../../shared/schemas/profile-schema'
import apiClient from '../../lib/util/api-client'
import { crudActions } from '../factories'
const { profileSchemaDeleted, profileSchemaUserTypes } = schemaSettings

const duplicatesService = apiClient.service('profiles/duplicates')

export const { find, setList, clearList, get, save, remove } = crudActions({ name: 'profiles' })

export const loadById = ({ props: { mobileUserIds, profileIds }, get }) => {
  if (mobileUserIds) {
    const ids = mobileUserIds.filter(Boolean).map((id) => ({ id, profile: get(state`profiles.mobileUserIdToProfileId.${id}`) }))
    const { missing = [] } = groupBy(({ profile }) => (profile ? 'present' : 'missing'), ids)
    return { useRouteParams: false, query: { mobileUser: missing.length ? { $in: uniq(pluck('id', missing)) } : null } }
  } else if (profileIds) {
    const ids = profileIds.filter(Boolean).map((id) => ({ id, profile: get(state`profiles.entities.${id}`) }))
    const { missing = [] } = groupBy(({ profile }) => (profile ? 'present' : 'missing'), ids)
    return { useRouteParams: false, query: { _id: missing.length ? { $in: uniq(pluck('id', missing)) } : null } }
  }
}

export const adjustFilters = ({ props: { useRouteParams, query = {} }, get }) => {
  // This logic is duplicated from crudActions.
  if (useRouteParams) {
    const params = get(state`route.params`) || {}
    const querystring = get(state`route.query`) || {}
    useRouteParams = false
    query = { ...query, ...params, ...querystring }
  }

  let deleted = { $ne: true }
  if (query.userTypes === 'deactivated') {
    deleted = true
    query.userTypes = undefined
  }
  if (query.userTypes === 'requesting') {
    query.filter = query.userTypes
    query.userTypes = undefined
  }
  query.deleted = deleted
  return { query, useRouteParams }
}

export const saveMobileUserMapping = ({ props: { entities: { profiles = [] } = {} }, store }) => {
  const idMap = fromPairs(profiles.filter(prop('mobileUser')).map(({ _id, mobileUser }) => [mobileUser._id || mobileUser, _id]))
  store.merge(state`profiles.mobileUserIdToProfileId`, idMap)
}

export const updateUserTypes = async ({ props: { id, add = [], remove = [], clear = false }, profilesService }) => {
  let data = {}
  if (clear) {
    data = { userTypes: [] }
  } else if (add.length) {
    const { userTypes: toAdd } = profileSchemaUserTypes.validateSync({ userTypes: add })
    data = { $addToSet: { userTypes: { $each: toAdd } } }
  } else if (remove.length) {
    const { userTypes: toRemove } = profileSchemaUserTypes.validateSync({ userTypes: remove })
    data = { $pull: { userTypes: { $in: toRemove } } }
  }
  const profile = await profilesService.patch(id, data)
  return { entities: { profiles: [profile] } }
}

export const updateRequestedUserTypes = async ({ props: { id, requestedUserTypes = [] }, profilesService }) => {
  const profile = await profilesService.patch(id, { requestedUserTypes })
  return { entities: { profiles: [profile] } }
}

export const manageProfileRequestingRoles = async ({ props: { profile }, store, get }) => {
  const { _id, requestedUserTypes = [] } = profile
  let requestingRoleIds = get(state`profiles.requestingRoleIds`)
  if (requestedUserTypes.length > 0) {
    requestingRoleIds = uniq(requestingRoleIds.concat(_id))
  } else {
    requestingRoleIds = difference(requestingRoleIds, [_id])
  }
  store.set(state`profiles.requestingRoleIds`, requestingRoleIds)
  return { entities: { profiles: [profile] } }
}

export const reactivate = async ({ props: { id }, profilesService }) => {
  const data = await profileSchemaDeleted.validate({ deleted: false, deletedAt: null, deletedBy: null })
  const profile = await profilesService.patch(id, data)
  return { entities: { profiles: [profile] } }
}

export const patch = async ({ props: { id, values }, profilesService, profilesValidation }) => {
  const profile = await profilesService.patch(id, values)
  return { entities: { profiles: [profilesValidation.cast(profile)] } }
}

export const acknowledgeBirthday = async ({ props: { id }, profilesService }) => {
  const profile = await profilesService.patch(id, { birthdateAcknowledgedAt: new Date().getFullYear() })
  return { entities: { profiles: [profile] } }
}

export const getTodaysBirthdays = async ({ profilesService: service, store, get }) => {
  const currentUser = get(state`account.currentUser`)
  if (currentUser?.canAccessCommandCenter) {
    const { data: birthdays = [] } = await service.find({ filter: 'birthdays' })
    store.set(moduleState`todaysBirthdays`, birthdays)
  }
}

export const setLevelOfCare = ({ props: { value }, store }) => {
  store.set(moduleState`filter.levelOfCare`, value)
}

export const checkDuplicates = async ({ props: { values = [] }, store }) => {
  values = [].concat(values).filter(Boolean)

  const $or = flatten(
    values.map(({ email, phone, name: { first, last } = {} } = {}) => {
      return [email && { email }, phone && { phone }, first && last && { 'name.first': first, 'name.last': last }].filter(Boolean)
    })
  )

  if (!$or.length) {
    store.set(moduleState`duplicates`, { count: 0, profiles: [] })
    return
  }

  // Split into multiple requests, so our payload isn't too large.
  const batches = splitEvery(50, $or)
  let profiles = []
  for (const batch of batches) {
    const result = await duplicatesService.find({
      query: {
        $or: batch,
        deleted: { $ne: true },
      },
    })
    profiles = profiles.concat(result)
  }
  profiles = uniqBy(prop('_id'), profiles)

  const count = profiles.length
  store.set(moduleState`duplicates`, { count, profiles })
  return { count }
}
